import { Check, Loader2, ShieldAlert, Undo2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, promptModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDateTime, fmtMoney } from '../shared/format'
import { Section } from './components/FinanceBits'
import { FINANCE_VOID_APPROVE, FINANCE_VOID_OWNER_APPROVE } from './permissions'
import { toastError } from './ui'
import { useApprovalSettings, useApproveVoid, useCancelVoid, useRejectVoid, useSaveApprovalSettings, useVoidRequests, type VoidRequest, type VoidStatus } from './voids'

const FILTERS = ['pending', 'approved', 'rejected', 'all'] as const
type Filter = (typeof FILTERS)[number]

const STATUS_TONE: Record<VoidStatus, BadgeTone> = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'neutral', failed: 'danger' }

/**
 * Finans iptal talepleri. Onaycı eşik altı talepleri, firma sahibi tümünü sonuçlandırır;
 * yetkisi olmayan kullanıcı yalnız kendi taleplerini görür ve geri çekebilir.
 */
export function VoidRequestsPage() {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const isOwner = can(FINANCE_VOID_OWNER_APPROVE)
  const isApprover = isOwner || can(FINANCE_VOID_APPROVE)
  const [filter, setFilter] = useState<Filter>('pending')
  const list = useVoidRequests(filter === 'all' ? '' : filter)
  const approve = useApproveVoid()
  const reject = useRejectVoid()
  const cancel = useCancelVoid()

  const canDecide = (r: VoidRequest) => r.status === 'pending' && (isOwner || (isApprover && r.level === 'approver' && r.requestedBy !== user?.id))

  const onApprove = async (r: VoidRequest) => {
    const ok = await confirmModal({
      title: t('finance.voids.approveTitle'),
      description: t('finance.voids.approveBody', { summary: r.summary, amount: fmtMoney(r.amount) }),
      confirmLabel: t('finance.voids.approve'),
      destructive: true,
    })
    if (ok) approve.mutate({ id: r.id }, { onSuccess: () => toast.success(t('finance.voids.approved')), onError: toastError })
  }
  const onReject = async (r: VoidRequest) => {
    const note = await promptModal({ title: t('finance.voids.rejectTitle'), placeholder: t('finance.voids.rejectPlaceholder'), confirmLabel: t('finance.voids.reject') })
    if (note == null) return
    if (!note.trim()) {
      toast.error(t('finance.voids.rejectNoteRequired'))
      return
    }
    reject.mutate({ id: r.id, note: note.trim() }, { onSuccess: () => toast.success(t('finance.voids.rejected')), onError: toastError })
  }
  const onCancel = (r: VoidRequest) => cancel.mutate(r.id, { onSuccess: () => toast.success(t('finance.voids.cancelled')), onError: toastError })

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.finance')} title={t('finance.voids.title')} description={t(isApprover ? 'finance.voids.subtitleApprover' : 'finance.voids.subtitleOwn')} />
      <div className="space-y-4">
        {isApprover ? <ThresholdCard editable={isOwner} /> : null}
        <Section
          title={t('finance.voids.listTitle')}
          bodyClassName="p-0"
          actions={
            <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <TabsList size="sm">
                {FILTERS.map((f) => (
                  <TabsTrigger key={f} value={f}>
                    {t(`finance.voids.filter.${f}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          }
        >
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : list.isLoading ? (
            <Skeleton className="m-4 h-40" />
          ) : !list.data?.length ? (
            <EmptyState icon={ShieldAlert} title={t('finance.voids.empty')} />
          ) : (
            <ul className="divide-y">
              {list.data.map((r) => (
                <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-0 flex-1 basis-64 space-y-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      <span className="truncate">{r.summary || t(`finance.voids.target.${r.targetType}`)}</span>
                      <Badge tone="neutral">{t(`finance.voids.target.${r.targetType}`)}</Badge>
                      <Badge tone={r.level === 'owner' ? 'info' : 'primary'}>{t(`finance.voids.level.${r.level}`)}</Badge>
                      <Badge tone={STATUS_TONE[r.status]}>{t(`finance.voids.status.${r.status}`)}</Badge>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">{t('finance.voids.reason')}:</span> {r.reason}
                    </p>
                    {r.escalationReasons.length ? (
                      <p className="text-xs text-muted-foreground">
                        {t('finance.voids.escalatedBecause')}: {r.escalationReasons.map((x) => t(`finance.voids.escalation.${x}`)).join(', ')}
                      </p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">
                      {t('finance.voids.requestedBy', { name: r.requestedByName || '—', at: fmtDateTime(r.createdAt), period: r.targetPeriod })}
                      {r.decidedAt ? ` · ${t('finance.voids.decidedBy', { name: r.decidedByName || '—', at: fmtDateTime(r.decidedAt) })}` : ''}
                    </p>
                    {r.decisionNote ? <p className="text-xs text-muted-foreground">{t('finance.voids.decisionNote')}: {r.decisionNote}</p> : null}
                  </div>
                  <span className="w-32 text-right font-semibold tabular-nums">{fmtMoney(r.amount)}</span>
                  <div className="flex flex-wrap gap-2">
                    {canDecide(r) ? (
                      <>
                        <Button size="sm" variant="destructive" onClick={() => void onApprove(r)} disabled={approve.isPending}>
                          <Check />
                          {t('finance.voids.approve')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => void onReject(r)} disabled={reject.isPending}>
                          <X />
                          {t('finance.voids.reject')}
                        </Button>
                      </>
                    ) : null}
                    {r.status === 'pending' && r.requestedBy === user?.id ? (
                      <Button size="sm" variant="ghost" onClick={() => onCancel(r)} disabled={cancel.isPending}>
                        <Undo2 />
                        {t('finance.voids.withdraw')}
                      </Button>
                    ) : null}
                    {r.status === 'pending' && !canDecide(r) && r.requestedBy !== user?.id && isApprover ? (
                      <span className="text-xs text-muted-foreground">{t('finance.voids.ownerOnly')}</span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  )
}

function ThresholdCard({ editable }: { editable: boolean }) {
  const { t } = useTranslation()
  const settings = useApprovalSettings(true)
  const save = useSaveApprovalSettings()
  const [value, setValue] = useState<string | null>(null)
  const current = settings.data?.ownerThreshold ?? ''
  const shown = value ?? current
  return (
    <Section title={t('finance.voids.thresholdTitle')} icon={ShieldAlert}>
      <p className="mb-3 text-sm text-muted-foreground">{t('finance.voids.thresholdHint')}</p>
      {editable ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="void-threshold">{t('finance.voids.threshold')}</Label>
            <Input id="void-threshold" inputMode="decimal" className="w-48" value={shown} onChange={(e) => setValue(e.target.value)} />
          </div>
          <Button
            size="sm"
            disabled={value == null || value === current || !/^\d+(\.\d{1,2})?$/.test(shown) || save.isPending}
            onClick={() => save.mutate(shown, { onSuccess: () => { setValue(null); toast.success(t('finance.voids.thresholdSaved')) }, onError: toastError })}
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      ) : (
        <p className="text-lg font-semibold tabular-nums">{current ? fmtMoney(current) : '—'}</p>
      )}
    </Section>
  )
}
